/**
 * Renaming a selection, as a file manager does: one item, a new name; several, a rule for all (replace some text, add
 * some before or after, or a name of one's own numbered or dated), seen on a few of them before it's done. A file's
 * extension stays out of it (the rule changes the name before it). One change on the server: one history entry.
 */
import { useEffect, useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { BulkRenamed, ItemRow, PageItemRow, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { ignore } from "../../lib/ignore.ts";
import { spaceApi } from "../../lib/paths.ts";
import { type Format, type Mode, type Rule, renamed, type Where } from "../../lib/renaming.ts";
import { useAction } from "../../lib/resource.ts";
import { Button } from "../../ui/Button.tsx";
import { DialogActions } from "../../ui/Dialog.tsx";
import { Field, Form, Input, Select } from "../../ui/form.tsx";
import { BecomesIcon } from "../../ui/icons.tsx";
import { Segmented } from "../../ui/Segmented.tsx";
import { Spinner } from "../../ui/Spinner.tsx";

/** How many items a page of their titles has (the list's most). */
const PAGE = 200;
/** How many renamed items the preview shows. */
const PREVIEWED = 5;

/** The selected items' titles (a page of them at a time), in the order they were picked. */
function useTitles(space: SpaceOut, ids: readonly string[], archived: boolean) {
  const [rows, setRows] = useState<ItemRow[] | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, for the selection the dialog opened on.
  useEffect(() => {
    const pages: Promise<PageItemRow>[] = [];
    for (let i = 0; i < ids.length; i += PAGE) {
      const chunk = ids.slice(i, i + PAGE);
      pages.push(
        api.get<PageItemRow>(spaceApi(space, "/items"), {
          ids: chunk.join(","),
          limit: chunk.length,
          in_files: "include",
          ...(archived ? { archived: true } : {}),
        }),
      );
    }
    Promise.all(pages).then((got) => {
      const byId = new Map(got.flatMap((p) => p.data).map((r) => [r.id, r]));
      setRows(ids.flatMap((id) => byId.get(id) ?? []));
    }, ignore);
  }, []);
  return rows;
}

/** The dialog's content: the rule (or a name), what it makes of a few of them, and Rename. */
export function RenameForm(p: {
  space: SpaceOut;
  ids: readonly string[];
  archived: boolean;
  onCancel: () => void;
  onDone: (r: BulkRenamed) => void;
}) {
  const rows = useTitles(p.space, p.ids, p.archived);
  const [rule, setRule] = useState<Rule>({
    mode: "replace",
    find: "",
    replace: "",
    text: "",
    where: "after",
    format: "index",
    name: t("rename.defaultName"),
    start: 1,
  });
  const [single, setSingle] = useState<string | null>(null);
  const set = (part: Partial<Rule>) => setRule({ ...rule, ...part });
  const one = rows?.length === 1 ? rows[0] : undefined;
  const titles = rows?.map((r, i) => (one ? (single ?? r.title ?? "") : renamed(rule, r, i))) ?? [];
  const changed = (rows ?? []).flatMap((r, i) => {
    const title = titles[i]?.trim() ?? "";
    return title && title !== r.title ? [{ id: r.id, title }] : [];
  });
  const [send, sending] = useAction(async () => {
    p.onDone(await api.post<BulkRenamed>(spaceApi(p.space, "/items/bulk/rename"), { items: changed }));
  });
  if (!rows) {
    return <Spinner />;
  }
  const files = rows.some((r) => r.kind === "file");
  return (
    <Form class="flex flex-col gap-4" onSubmit={() => (changed.length > 0 ? send() : p.onCancel())}>
      {one ? (
        <Field label={t("items.title")}>
          <Input value={single ?? one.title ?? ""} onValue={setSingle} autoFocus={true} maxLength={1000} />
        </Field>
      ) : (
        <>
          <Segmented<Mode>
            label={t("rename.how")}
            value={rule.mode}
            onValue={(mode) => set({ mode })}
            options={[
              { value: "replace", label: t("rename.replace") },
              { value: "add", label: t("rename.add") },
              { value: "format", label: t("rename.format") },
            ]}
          />
          {rule.mode === "replace" ? (
            <div class="grid gap-4 sm:grid-cols-2">
              <Field label={t("rename.find")}>
                <Input value={rule.find} onValue={(find) => set({ find })} autoFocus={true} />
              </Field>
              <Field label={t("rename.replaceWith")}>
                <Input value={rule.replace} onValue={(replace) => set({ replace })} />
              </Field>
            </div>
          ) : rule.mode === "add" ? (
            <div class="grid gap-4 sm:grid-cols-2">
              <Field label={t("rename.text")}>
                <Input value={rule.text} onValue={(text) => set({ text })} autoFocus={true} />
              </Field>
              <WhereField where={rule.where} onWhere={(where) => set({ where })} />
            </div>
          ) : (
            <div class="grid gap-4 sm:grid-cols-2">
              <Field label={t("rename.nameFormat")}>
                <Select
                  value={rule.format}
                  onValue={(v) => set({ format: v as Format })}
                  options={[
                    { value: "index", label: t("rename.index") },
                    { value: "counter", label: t("rename.counter") },
                    { value: "date", label: t("rename.date") },
                  ]}
                />
              </Field>
              <WhereField where={rule.where} onWhere={(where) => set({ where })} />
              <Field label={t("rename.custom")}>
                <Input value={rule.name} onValue={(name) => set({ name })} autoFocus={true} />
              </Field>
              {rule.format === "date" ? null : (
                <Field label={t("rename.startAt")}>
                  <Input
                    type="number"
                    min={0}
                    value={String(rule.start)}
                    onValue={(v) => set({ start: Math.max(0, Math.trunc(Number(v) || 0)) })}
                  />
                </Field>
              )}
            </div>
          )}
          <section aria-label={t("rename.preview")} class="flex flex-col gap-1 text-sm">
            <h3 class="text-xs font-medium text-zinc-500">{t("rename.preview")}</h3>
            <ul class="flex flex-col gap-0.5">
              {rows.slice(0, PREVIEWED).map((r, i) => (
                <li key={r.id} class="flex min-w-0 items-baseline gap-2">
                  <span class="min-w-0 truncate text-zinc-500">{r.title}</span>
                  <BecomesIcon size={14} class="shrink-0 self-center text-zinc-400" />
                  <span class="min-w-0 truncate font-medium text-zinc-900">{titles[i]}</span>
                </li>
              ))}
            </ul>
            {rows.length > PREVIEWED ? (
              <p class="text-xs text-zinc-500">{t("rename.more", { count: rows.length - PREVIEWED })}</p>
            ) : null}
          </section>
        </>
      )}
      {files ? <p class="text-xs text-zinc-500">{t("rename.keepsExtension")}</p> : null}
      <DialogActions>
        <Button answer="cancel" onClick={p.onCancel}>
          {t("common.cancel")}
        </Button>
        <Button answer="confirm" variant="primary" submit={true} busy={sending} disabled={changed.length === 0}>
          {t("item.rename")}
        </Button>
      </DialogActions>
    </Form>
  );
}

function WhereField(p: { where: Where; onWhere: (w: Where) => void }) {
  return (
    <Field label={t("rename.where")}>
      <Select
        value={p.where}
        onValue={(v) => p.onWhere(v as Where)}
        options={[
          { value: "after", label: t("rename.afterName") },
          { value: "before", label: t("rename.beforeName") },
        ]}
      />
    </Field>
  );
}
